import type { AudioManifest, DinConfig } from "@hearing/shared";

/**
 * Web Audio engine for the Digits-in-Noise test.
 *
 * Signal chain:
 *   digit buffer -> digitGain ┐
 *                              ├─> masterGain -> destination
 *   noise buffer -> noiseGain ┘
 *
 * The digit level is held fixed; the noise level is set per-trial to achieve
 * the requested SNR. masterGain is the user's comfortable volume from the
 * calibration step. Relative gains map to dB via 20*log10(gain).
 */
export class DinEngine {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;
  private digitBuffers = new Map<number, AudioBuffer>();
  private noiseBuffer: AudioBuffer | null = null;
  private readonly manifest: AudioManifest;
  private readonly din: DinConfig;
  private masterLevel = 0.6;
  private toneOsc: OscillatorNode | null = null;
  private toneGain: GainNode | null = null;
  private tonePulse: number | null = null;

  constructor(manifest: AudioManifest, din: DinConfig) {
    this.manifest = manifest;
    this.din = din;
  }

  /** Must be called from a user gesture to satisfy autoplay policies. */
  async init(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === "suspended") await this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.masterLevel;
    this.masterGain.connect(this.ctx.destination);
    await this.loadAssets();
  }

  private async loadAssets(): Promise<void> {
    const ctx = this.ctx!;
    const load = async (url: string): Promise<AudioBuffer> => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load audio: ${url}`);
      const arr = await res.arrayBuffer();
      return await ctx.decodeAudioData(arr);
    };

    // Only the noise loop is needed now (for the headphone check). Tones are
    // synthesized with an oscillator, so no digit clips are loaded.
    const base = this.manifest.basePath.replace(/\/$/, "");
    this.noiseBuffer = await load(`${base}/${this.manifest.noise}`);
  }

  setMasterLevel(level: number): void {
    this.masterLevel = Math.max(0, Math.min(1, level));
    if (this.masterGain) this.masterGain.gain.value = this.masterLevel;
  }

  getMasterLevel(): number {
    return this.masterLevel;
  }

  /** SNR (dB) -> noise gain, given the fixed digit gain. */
  private noiseGainForSnr(snrDb: number): number {
    return this.din.digitGain / Math.pow(10, snrDb / 20);
  }

  private makeNoiseSource(
    durationSec: number,
    gainValue: number,
    pan: number,
  ): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer!;
    src.loop = true;
    // Random start offset so the noise sample differs each trial.
    src.loopStart = 0;
    const offset = Math.random() * (this.noiseBuffer!.duration || 1);
    const gain = ctx.createGain();
    gain.gain.value = gainValue;
    this.connectWithPan(src, gain, pan);
    src.start(ctx.currentTime, offset % this.noiseBuffer!.duration);
    src.stop(ctx.currentTime + durationSec);
    return src;
  }

  private connectWithPan(
    node: AudioNode,
    gain: GainNode,
    pan: number,
  ): void {
    node.connect(gain);
    if (pan !== 0 && typeof this.ctx!.createStereoPanner === "function") {
      const panner = this.ctx!.createStereoPanner();
      panner.pan.value = pan;
      gain.connect(panner);
      panner.connect(this.masterGain);
    } else {
      gain.connect(this.masterGain);
    }
  }

  /**
   * Play a digit triplet mixed with noise at the given SNR.
   * Resolves when playback finishes.
   */
  async playTriplet(digits: number[], snrDb: number): Promise<void> {
    const ctx = this.ctx!;
    const digitGap = 0.35;
    const leadIn = 0.5;

    let cursor = ctx.currentTime + leadIn;
    let lastEnd = cursor;
    for (const d of digits) {
      const buf = this.digitBuffers.get(d);
      if (!buf) continue;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const gain = ctx.createGain();
      gain.gain.value = this.din.digitGain;
      src.connect(gain);
      gain.connect(this.masterGain);
      src.start(cursor);
      lastEnd = cursor + buf.duration;
      cursor = lastEnd + digitGap;
    }

    const totalDuration = lastEnd - ctx.currentTime + 0.5;
    this.makeNoiseSource(totalDuration, this.noiseGainForSnr(snrDb), 0);

    await this.sleep(totalDuration * 1000);
  }

  /** Play noise + a sample triplet on loop for volume calibration. */
  async playCalibration(digits: number[]): Promise<void> {
    await this.playTriplet(digits, this.din.initialSnrDb);
  }

  /** Play a short noise burst panned fully to one ear for the headphone check. */
  async playChannelCheck(side: "left" | "right"): Promise<void> {
    const pan = side === "left" ? -1 : 1;
    this.makeNoiseSource(1.2, 0.4, pan);
    await this.sleep(1200);
  }

  /**
   * Play a pure sine tone at a given frequency, attenuation and ear for the
   * audiogram test. `attenuationDb` is applied below a reference level; higher
   * attenuation = quieter. Short raised-cosine fades avoid audible clicks.
   */
  async playTone(
    frequency: number,
    attenuationDb: number,
    ear: "left" | "right",
    durationSec = 1.0,
  ): Promise<void> {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = frequency;

    // Reference peak gain for 0 dB attenuation, then attenuate.
    const reference = 0.5;
    const level = reference * Math.pow(10, -attenuationDb / 20);

    const gain = ctx.createGain();
    const t = ctx.currentTime;
    const fade = 0.02;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(level, t + fade);
    gain.gain.setValueAtTime(level, t + durationSec - fade);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + durationSec);

    osc.connect(gain);
    this.panTo(gain, ear === "left" ? -1 : 1);

    osc.start(t);
    osc.stop(t + durationSec + 0.02);
    await this.sleep(durationSec * 1000 + 60);
  }

  /**
   * Start a continuous pulsing tone (~1s on / 0.3s off) whose level can be
   * adjusted live with setToneAttenuation while it plays. Chain:
   *   osc -> toneGain (level) -> pulseGain (on/off envelope) -> pan -> master
   * Pulsing lives on its own gain node so live level changes never fight the
   * pulse envelope ramps.
   */
  startTone(
    frequency: number,
    attenuationDb: number,
    ear: "left" | "right",
  ): void {
    this.stopTone();
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = frequency;

    const toneGain = ctx.createGain();
    toneGain.gain.value = this.toneLevelFor(attenuationDb);

    const pulseGain = ctx.createGain();
    pulseGain.gain.value = 0.0001;

    osc.connect(toneGain);
    toneGain.connect(pulseGain);
    this.panTo(pulseGain, ear === "left" ? -1 : 1);
    osc.start();

    const onSec = 1.0;
    const periodMs = 1300;
    const fade = 0.02;
    const schedulePulse = () => {
      const t = ctx.currentTime;
      pulseGain.gain.cancelScheduledValues(t);
      pulseGain.gain.setValueAtTime(0.0001, t);
      pulseGain.gain.exponentialRampToValueAtTime(1, t + fade);
      pulseGain.gain.setValueAtTime(1, t + onSec - fade);
      pulseGain.gain.exponentialRampToValueAtTime(0.0001, t + onSec);
    };
    schedulePulse();
    this.toneOsc = osc;
    this.toneGain = toneGain;
    this.tonePulse = window.setInterval(schedulePulse, periodMs);
  }

  /** Live-adjust the level of the tone started with startTone. */
  setToneAttenuation(attenuationDb: number): void {
    if (!this.toneGain || !this.ctx) return;
    this.toneGain.gain.setTargetAtTime(
      this.toneLevelFor(attenuationDb),
      this.ctx.currentTime,
      0.03,
    );
  }

  stopTone(): void {
    if (this.tonePulse != null) {
      clearInterval(this.tonePulse);
      this.tonePulse = null;
    }
    if (this.toneOsc && this.ctx) {
      const t = this.ctx.currentTime;
      try {
        this.toneGain?.gain.cancelScheduledValues(t);
        this.toneGain?.gain.setTargetAtTime(0.0001, t, 0.02);
        this.toneOsc.stop(t + 0.1);
      } catch {
        // Already stopped.
      }
    }
    this.toneOsc = null;
    this.toneGain = null;
  }

  isTonePlaying(): boolean {
    return this.toneOsc !== null;
  }

  private toneLevelFor(attenuationDb: number): number {
    const reference = 0.5;
    return reference * Math.pow(10, -attenuationDb / 20);
  }

  private panTo(node: AudioNode, pan: number): void {
    if (typeof this.ctx!.createStereoPanner === "function") {
      const panner = this.ctx!.createStereoPanner();
      panner.pan.value = pan;
      node.connect(panner);
      panner.connect(this.masterGain);
    } else {
      node.connect(this.masterGain);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  close(): void {
    this.stopTone();
    this.ctx?.close();
    this.ctx = null;
  }
}
